import Select from '@components/V4/Select/Select';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { HubspotContractFiltersDTO } from '@shared/apis/hubspotContracts.apis';
import { TriState } from '@shared/clientTypes/misc';
import moment from 'moment';

export type ContractFilterState = {
  hasContract?: boolean;
  stage?: string;
  property?: string;
  value?: string;
};

type Props = {
  filters?: HubspotContractFiltersDTO;
  isLoading: boolean;
  state: ContractFilterState;
  onChange: (patch: Partial<ContractFilterState>) => void;
};

const toTriState = (value?: boolean): TriState =>
  value === undefined ? 'indeterminate' : value ? 'checked' : 'unchecked';

const ContractFilters = (props: Props) => {
  const { filters, isLoading, state, onChange } = props;
  const selectedProperty = filters?.properties.find((item) => item.name === state.property);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-sm font-semibold text-white">Contract (HubSpot)</span>
        <span className="text-xs text-customGrey">
          {filters?.configured === false
            ? 'HubSpot is not configured on this environment.'
            : filters?.lastSyncedAt
            ? `${filters.studentsWithContract} students matched to a deal · synced ${moment(
                filters.lastSyncedAt
              ).fromNow()}`
            : 'Not synced yet. Use "Sync HubSpot contracts" to pull the Contracts Pipeline.'}
        </span>
      </div>
      <div className="flex flex-wrap items-end gap-6">
        <TriStateToggle
          title="HubSpot contract:"
          options={[
            { label: 'Without', value: 'unchecked' },
            { label: 'With', value: 'checked' },
            { label: 'All', value: 'indeterminate' },
          ]}
          value={toTriState(state.hasContract)}
          onChange={(value) =>
            onChange({ hasContract: value === 'indeterminate' ? undefined : value === 'checked' })
          }
        />
        <div className="flex flex-col w-64 max-w-64">
          <Select
            className="max-w-64"
            variant="secondary"
            label="Contract stage"
            isLoading={isLoading}
            allowClear
            value={state.stage}
            options={(filters?.stages ?? []).map((stage) => ({
              value: stage.id,
              label: `${stage.label} (${stage.count})`,
            }))}
            onSelect={(value) => onChange({ stage: value ? String(value) : undefined })}
            onClear={() => onChange({ stage: undefined })}
            placeholder="Any stage"
          />
        </div>
        <div className="flex flex-col w-64 max-w-64">
          <Select
            className="max-w-64"
            variant="secondary"
            label="Deal field"
            isLoading={isLoading}
            allowClear
            showSearch
            value={state.property}
            options={(filters?.properties ?? []).map((property) => ({
              value: property.name,
              label: property.label,
            }))}
            onSelect={(value) =>
              onChange({ property: value ? String(value) : undefined, value: undefined })
            }
            onClear={() => onChange({ property: undefined, value: undefined })}
            placeholder="Payment type, status…"
          />
        </div>
        <div className="flex flex-col w-64 max-w-64">
          <Select
            className="max-w-64"
            variant="secondary"
            label="Field value"
            allowClear
            showSearch
            isDisabled={!selectedProperty}
            value={state.value}
            options={(selectedProperty?.values ?? []).map((entry) => ({
              value: entry.value,
              label: `${entry.value} (${entry.count})`,
            }))}
            onSelect={(value) => onChange({ value: value ? String(value) : undefined })}
            onClear={() => onChange({ value: undefined })}
            placeholder={selectedProperty ? 'Any value' : 'Pick a field first'}
          />
        </div>
      </div>
    </div>
  );
};

export default ContractFilters;
